// adminportal/src/services/mfaService.ts
import { apiUrl } from '../config';

export interface MfaAssociateResult {
  success: boolean;
  secretCode?: string;
  session?: string;
  error?: string;
}

export interface MfaVerifyResult {
  success: boolean;
  error?: string;
}

export interface MfaSetPreferenceResult {
  success: boolean;
  error?: string;
}

export class MfaService {
  /**
   * Associate a software token (TOTP) with the user's account
   * This returns a secret code that the user can enter into their authenticator app
   */
  static async associateSoftwareToken(accessToken: string): Promise<MfaAssociateResult> {
    try {
      console.log('🔍 MfaService: Calling associate-software-token API...');

      const response = await fetch(apiUrl('mfa/associate-software-token'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken }),
      });

      console.log('🔍 MfaService: Associate response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.log('🔍 MfaService: Associate error:', errorData);
        return {
          success: false,
          error: errorData.error || 'Failed to associate MFA token',
        };
      }

      const data = await response.json();
      console.log('🔍 MfaService: Associate response data:', data);

      return {
        success: true,
        secretCode: data.secretCode,
        session: data.session,
      };
    } catch (error) {
      console.error('🔍 MfaService: Associate error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Verify a software token code entered by the user
   */
  static async verifySoftwareToken(
    accessToken: string,
    userCode: string
  ): Promise<MfaVerifyResult> {
    try {
      console.log('🔍 MfaService: Calling verify-software-token API...');

      const response = await fetch(apiUrl('mfa/verify-software-token'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken, userCode }),
      });

      console.log('🔍 MfaService: Verify response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.log('🔍 MfaService: Verify error response:', response.status, errorData);
        console.log('🔍 MfaService: Request details:', {
          accessToken: `${accessToken?.substring(0, 20)}...`,
          userCode,
        });
        return {
          success: false,
          error: errorData.error || `Failed to verify MFA code (${response.status})`,
        };
      }

      console.log('🔍 MfaService: Verify successful');
      return { success: true };
    } catch (error) {
      console.error('🔍 MfaService: Verify error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Set MFA preference to enabled for the user
   */
  static async setMfaPreference(accessToken: string): Promise<MfaSetPreferenceResult> {
    try {
      console.log('🔍 MfaService: Calling set-mfa-preference API...');

      const response = await fetch(apiUrl('mfa/set-mfa-preference'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken }),
      });

      console.log('🔍 MfaService: Set preference response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.log('🔍 MfaService: Set preference error:', errorData);
        return {
          success: false,
          error: errorData.error || 'Failed to set MFA preference',
        };
      }

      console.log('🔍 MfaService: Set preference successful');
      return { success: true };
    } catch (error) {
      console.error('🔍 MfaService: Set preference error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Complete MFA setup: associate token, verify code, and set preference
   */
  static async completeMfaSetup(
    accessToken: string,
    userCode: string
  ): Promise<{
    success: boolean;
    secretCode?: string;
    error?: string;
  }> {
    try {
      // Step 1: Associate software token
      const associateResult = await this.associateSoftwareToken(accessToken);
      if (!associateResult.success) {
        return {
          success: false,
          error: associateResult.error,
        };
      }

      // Step 2: Verify the code
      const verifyResult = await this.verifySoftwareToken(accessToken, userCode);
      if (!verifyResult.success) {
        return {
          success: false,
          secretCode: associateResult.secretCode, // Return the secret so user can try again
          error: verifyResult.error,
        };
      }

      // Step 3: Set MFA preference
      const preferenceResult = await this.setMfaPreference(accessToken);
      if (!preferenceResult.success) {
        return {
          success: false,
          secretCode: associateResult.secretCode,
          error: preferenceResult.error,
        };
      }

      return {
        success: true,
        secretCode: associateResult.secretCode,
      };
    } catch (error) {
      console.error('🔍 MfaService: Complete setup error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }
}
